// A6 — Kontent moderatsiyasi (A6.09–A6.42). Module-local data layer.
// `lib/adapters.ts` / `i18n/*` GA TEGMAYDI (C-session HOT). Backend
// `/admin/moderation/*` bir qismi greenfield → fetcher'lar fail-soft (bo'sh
// massiv), mutatsiyalar endpoint chiqqach ishlaydi. safeList = никогда throw.
import { api } from '@/lib/api'
import type { FetchParams, FetchResult, Fetcher } from '@/components/DataTable/types'

/* ── umumiy yordamchilar ──────────────────────────────────────────── */

function toResult<T>(rows: T[], params: FetchParams, search?: (r: T) => string): FetchResult<T> {
  const q = params.search.trim().toLowerCase()
  const filtered = q && search ? rows.filter((r) => search(r).toLowerCase().includes(q)) : rows
  const start = params.page * params.pageSize
  return { rows: filtered.slice(start, start + params.pageSize), total: filtered.length }
}

async function safeList<T>(
  path: string,
  key: string,
  signal: AbortSignal,
  query?: Record<string, string | number>,
): Promise<T[]> {
  try {
    const data = await api.get<Record<string, T[]>>(path, {
      signal,
      ...(query ? { query } : {}),
    })
    return (data?.[key] as T[] | undefined) ?? []
  } catch {
    return []
  }
}

/** List-ekranlar uchun fetcher fabrikasi (fail-soft + tez qidiruv). */
export function makeFetcher<T>(
  path: string,
  key: string,
  search?: (r: T) => string,
  query?: (params: FetchParams) => Record<string, string | number> | undefined,
): Fetcher<T> {
  return (params) =>
    safeList<T>(path, key, params.signal, query?.(params)).then((rows) => toResult(rows, params, search))
}

/* ── qator turlari ────────────────────────────────────────────────── */

/** A6.09 — bot / Mini App moderatsiya navbati. */
export interface BotModRow {
  id: number
  bot_username: string
  owner: string
  kind: 'bot' | 'miniapp'
  category: string
  status: string
  flags: number
  created_at: string
}

/** A6.10 — stiker to'plami / maxsus emoji moderatsiyasi. */
export interface StickerModRow {
  id: number
  set_title: string
  owner: string
  kind: 'sticker' | 'emoji'
  category: string
  status: string
  created_at: string
}

/** A6.11 — username spoofing / impersonatsiya navbati. */
export interface UsernameModRow {
  id: number
  username: string
  user_id: number
  similar_to: string
  kind: 'impersonation' | 'spoofing' | 'scam' | 'other'
  status: string
  created_at: string
}

/** A6.12 — avatar / profil rasmi moderatsiyasi. */
export interface AvatarModRow {
  id: number
  user_id: number
  entity: string
  category: string
  status: string
  created_at: string
}

/** A6.14 — ikkinchi ko'rib chiqish (appeal / second-review) navbati. */
export interface ReviewRow {
  id: number
  target_type: string
  target_id: number
  category: string
  first_decision: string
  requested_by: string
  status: string
  created_at: string
}

/** A6.16 — kontekst ko'rinishi: bayroqlangan xabar atrofidagi xabarlar. */
export interface ContextMsgRow {
  id: number
  seq: number
  sender: string
  text: string
  is_target: boolean
  sent_at: string
}

/** A6.17 — foydalanuvchining moderatsiya tarixi (yon panel). */
export interface UserHistoryRow {
  id: number
  kind: string
  category: string
  action: string
  actor: string
  created_at: string
}

/** A6.19 — yaqinda qabul qilingan qarorlar (forma ostidagi jurnal). */
export interface DecisionRow {
  id: number
  target_type: string
  target_id: number
  action: string
  reason: string
  moderator: string
  created_at: string
}

/** A6.22 — ikkinchi fikr so'rovlari. */
export interface SecondOpinionRow {
  id: number
  target_type: string
  target_id: number
  category: string
  asked_by: string
  question: string
  status: string
  created_at: string
}

/** A6.26 — ma'lum zararli kontent hash bazasi. */
export interface HashRow {
  id: number
  hash: string
  hash_type: 'md5' | 'sha256' | 'other'
  category: string
  source: string
  added_by: string
  created_at: string
}

/** A6.28 — kalit so'zlar ro'yxatlari. */
export interface KeywordListRow {
  id: number
  name: string
  lang: string
  entries: number
  category: string
  enabled: boolean
  updated_at: string
}

/** A6.31 — zararli fayl hash qora ro'yxati (APK reyestri). */
export interface FileHashRow {
  id: number
  sha256: string
  filename: string
  kind: 'apk' | 'exe' | 'archive' | 'other'
  threat: string
  added_by: string
  created_at: string
}

/** A6.32 — oq ro'yxatlar (false-positive'ni oldini olish). */
export interface WhitelistRow {
  id: number
  value: string
  kind: 'domain' | 'user' | 'word' | 'hash'
  reason: string
  added_by: string
  created_at: string
}

/** A6.33 — if-then qoidalar (konstruktor). */
export interface ModRuleRow {
  id: number
  name: string
  condition: string
  action: string
  category: string
  enabled: boolean
  hits: number
  updated_at: string
}

/** A6.37 — qarorlar auditi (tasodifiy tanlov). */
export interface AuditRow {
  id: number
  decision_id: number
  original_action: string
  auditor: string
  verdict: 'correct' | 'incorrect' | 'unsure' | 'pending'
  sampled_at: string
}

/** A6.39 — xato / bekor qilingan qarorlar. */
export interface ErrorDecisionRow {
  id: number
  decision_id: number
  original_action: string
  corrected_action: string
  reason: string
  moderator: string
  created_at: string
}

/** A6.40 — o'quv rejimidagi moderatorlar (shadow qarorlar). */
export interface TrainingRow {
  id: number
  moderator: string
  shadow_decisions: number
  accuracy_pct: number
  status: string
  started_at: string
}

/** A6.41 — moderatsiya siyosati (ichki qo'llanma). */
export interface PolicyRow {
  id: number
  title: string
  category: string
  version: string
  updated_by: string
  updated_at: string
}

/** A6.42 — shaffoflik hisobotlari. */
export interface TransparencyRow {
  id: number
  period: string
  actions_total: number
  removals: number
  appeals: number
  generated_by: string
  status: string
  generated_at: string
}

/* ── mutatsiyalar (endpoint chiqqach ishlaydi) ────────────────────── */

/** A6.19 — qaror yuborish. */
export const submitDecision = (body: {
  target_type: string
  target_id: number
  action: string
  reason: string
}) => api.post<{ id: number }>('/admin/moderation/decisions', body)

/** A6.34 — qoidani namunada sinash. */
export const testModRule = (pattern: string, is_regex: boolean, sample: string) =>
  api.post<{ matched: boolean }>('/admin/moderation/test', { pattern, is_regex, sample })

/** A6.33 — if-then qoida yaratish. */
export const createModRule = (body: {
  name: string
  condition: string
  action: string
  category: string
}) => api.post<{ id: number }>('/admin/moderation/rules', body)

/** A6.28 — kalit so'zlar ro'yxati qo'shish. */
export const addKeywordList = (body: { name: string; lang: string; category: string }) =>
  api.post<{ id: number }>('/admin/moderation/keyword-lists', body)

/** A6.28 — kalit so'zlar ro'yxatini o'chirish. */
export const deleteKeywordList = (id: number) => api.del(`/admin/moderation/keyword-lists/${id}`)

/** A6.22 — ikkinchi fikr berish. */
export const giveSecondOpinion = (id: number, opinion: 'agree' | 'disagree', note: string) =>
  api.post(`/admin/moderation/second-opinion/${id}/opinion`, { opinion, note })

/** A6.42 — shaffoflik hisobotini generatsiya qilish. */
export const generateTransparency = (period: string) =>
  api.post<{ id: number }>('/admin/moderation/transparency', { period })

/* ── modul-lokal satrlar (i18n HOT → keyга ko'chiriladi) ──────────── */

export const MOD_STR = {
  // umumiy
  empty: "Ma'lumot yo'q",
  created: 'Yaratilgan',
  updatedAt: 'Yangilangan',
  status: 'Holat',
  category: 'Toifa',
  action: 'Amal',
  reason: 'Sabab',
  owner: 'Egasi',
  kind: 'Turi',
  moderator: 'Moderator',
  failed: 'Amal bajarilmadi',
  done: 'Bajarildi',
  add: "Qo'shish",
  delete: "O'chirish",
  test: 'Sinash',
  generate: 'Generatsiya',
  target: 'Obyekt',
  // A6.09
  bots: 'Bot / Mini App moderatsiyasi',
  botsDesc: 'Bot va Mini App kontentini ko’rib chiqish navbati',
  botUsername: 'Bot',
  flags: 'Bayroqlar',
  // A6.10
  stickers: 'Stiker / emoji moderatsiyasi',
  stickersDesc: 'Stiker to’plamlari va maxsus emojilarni ko’rib chiqish',
  setTitle: "To'plam",
  // A6.11
  usernames: 'Username moderatsiyasi (spoofing)',
  usernamesDesc: 'Impersonatsiya / o’xshash username navbati',
  username: 'Username',
  similarTo: "O'xshash",
  // A6.12
  avatars: 'Avatar / rasm moderatsiyasi',
  avatarsDesc: 'Profil rasmlari va avatarlarni ko’rib chiqish',
  entity: 'Obyekt',
  // A6.14
  review: 'Ikkinchi ko’rib chiqish navbati',
  reviewDesc: 'Qayta ko’rib chiqishga yuborilgan qarorlar',
  firstDecision: 'Birinchi qaror',
  requestedBy: 'So’ragan',
  // A6.16
  context: 'Kontekst ko’rinishi',
  contextDesc: 'Bayroqlangan xabar atrofidagi xabarlar',
  contextPrompt: 'Xabar ID sini kiriting (?message=…) — kontekst chiqadi',
  seq: '#',
  sender: 'Yuboruvchi',
  text: 'Matn',
  sentAt: 'Vaqti',
  targetMsg: 'Bayroqlangan',
  // A6.17
  userHistory: 'Foydalanuvchi tarixi',
  userHistoryDesc: 'Moderatsiya harakatlari tarixi (yon panel)',
  userPrompt: 'Foydalanuvchi ID sini kiriting (?user=…)',
  actor: 'Kim',
  // A6.19
  decision: 'Qaror formasi',
  decisionDesc: 'Obyektga qaror qabul qilish (tugmalar + sabab)',
  targetType: 'Obyekt turi',
  targetId: 'Obyekt ID',
  reasonPh: 'Sabab (majburiy)…',
  submit: 'Qaror yuborish',
  recentDecisions: 'So’nggi qarorlar',
  decisionSaved: 'Qaror saqlandi',
  needTarget: 'Obyekt ID va sababni kiriting',
  // A6.22
  secondOpinion: 'Ikkinchi fikr so’rash',
  secondOpinionDesc: 'Boshqa moderatordan fikr so’ralgan holatlar',
  askedBy: 'So’ragan',
  question: 'Savol',
  agree: 'Qo’shilaman',
  disagree: 'Qo’shilmayman',
  opinionSaved: 'Fikr saqlandi',
  // A6.26
  hashes: 'Hash bazasi',
  hashesDesc: 'Ma’lum zararli kontent hash’lari',
  hash: 'Hash',
  hashType: 'Turi',
  source: 'Manba',
  addedBy: 'Kim qo’shdi',
  // A6.28
  keywords: 'Kalit so’zlar ro’yxatlari',
  keywordsDesc: 'Til / toifa bo’yicha kalit-so’z ro’yxatlari',
  listName: 'Ro’yxat nomi',
  lang: 'Til',
  entries: 'Elementlar',
  enabled: 'Yoqilgan',
  namePrompt: 'Ro’yxat nomi:',
  langPrompt: 'Til kodi (uz|ru|en):',
  listAdded: 'Ro’yxat qo’shildi',
  listDeleted: 'Ro’yxat o’chirildi',
  confirmDelete: 'Ro’yxatni o’chirasizmi?',
  // A6.31
  fileHashes: 'Fayl hash qora ro’yxati',
  fileHashesDesc: 'Zararli APK / fayl reyestri (SHA-256)',
  filename: 'Fayl',
  threat: 'Tahdid',
  // A6.32
  whitelists: 'Oq ro’yxatlar',
  whitelistsDesc: 'False-positive’ni oldini olish uchun ishonchli qiymatlar',
  value: 'Qiymat',
  // A6.33
  rules: 'Qoidalar konstruktori',
  rulesDesc: 'if-then qoidalar (shart → amal)',
  ruleName: 'Nomi',
  condition: 'Shart',
  hits: 'Ishga tushdi',
  addRule: 'Qoida qo’shish',
  ruleAdded: 'Qoida qo’shildi',
  needRule: 'Nomi, shart va amalni to’ldiring',
  // A6.34
  ruleTest: 'Qoida testi',
  ruleTestDesc: 'Shablon / regex ni namuna matnda sinash',
  pattern: 'Shablon',
  regex: 'Regex',
  sample: 'Namuna matn…',
  matched: 'Mos keldi ✓',
  noMatch: 'Mos kelmadi',
  // A6.37
  audit: 'Qarorlar auditi',
  auditDesc: 'Tasodifiy tanlab olingan qarorlarni tekshirish',
  decisionId: 'Qaror ID',
  originalAction: 'Asl amal',
  auditor: 'Auditor',
  verdict: 'Hukm',
  sampledAt: 'Tanlangan',
  // A6.39
  errors: 'Xato qarorlar tahlili',
  errorsDesc: 'Bekor qilingan / to’g’rilangan qarorlar',
  correctedAction: 'To’g’rilangan amal',
  // A6.40
  training: 'O’quv rejimi',
  trainingDesc: 'Yangi moderatorlar (shadow qaror, real ta’sir yo’q)',
  shadowDecisions: 'Shadow qarorlar',
  accuracy: 'Aniqlik %',
  startedAt: 'Boshlangan',
  // A6.41
  policies: 'Moderatsiya siyosati',
  policiesDesc: 'Ichki qo’llanma va siyosat hujjatlari',
  title: 'Sarlavha',
  version: 'Versiya',
  updatedBy: 'Kim yangiladi',
  // A6.42
  transparency: 'Shaffoflik hisoboti',
  transparencyDesc: 'Davriy shaffoflik hisobotlarini generatsiya qilish',
  period: 'Davr',
  actionsTotal: 'Jami amallar',
  removals: 'O’chirishlar',
  appeals: 'Appeal',
  generatedBy: 'Kim',
  generatedAt: 'Qachon',
  periodPrompt: 'Davr (masalan 2026-Q3):',
  reportQueued: 'Hisobot navbatga qo’yildi',
} as const
